import axios from "axios";
import { notifications } from "@/assets/utils/notifications";

const user = JSON.parse(localStorage.getItem("user")) || {};
const { VUE_APP_API_URL } = process.env;

// --- Session activity tracking (Tarea 3: 10-day inactivity logout) ---
const SESSION_MAX_INACTIVE_MS = 10 * 24 * 60 * 60 * 1000; // 10 days
const ACTIVITY_KEY = "last_activity";

function updateActivity() {
  localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
}

function checkInactivity() {
  const last = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10);
  if (last && Date.now() - last > SESSION_MAX_INACTIVE_MS) {
    forceLogout("Tu sesión expiró por inactividad. Inicia sesión nuevamente.");
    return true;
  }
  return false;
}

function forceLogout(message) {
  localStorage.removeItem("user");
  localStorage.removeItem(ACTIVITY_KEY);
  if (message) {
    localStorage.setItem("logout_reason", message);
  }
  window.location.href = "/login";
}

// Al cargar: validar token existente. Si es inválido, limpiar localStorage.
if (localStorage.getItem("user")) {
  try {
    const storedUser = JSON.parse(localStorage.getItem("user")) || {};
    if (storedUser.token && storedUser.token.split(".").length !== 3) {
      // Token corrupto — limpiar silenciosamente sin redirigir aún
      localStorage.removeItem("user");
      localStorage.removeItem(ACTIVITY_KEY);
    } else {
      updateActivity();
    }
  } catch (e) {
    localStorage.removeItem("user");
  }
}

function clearErrors() {
  // No-op preservado por compatibilidad histórica. La cuenta `_err_count`
  // ya no se setea en ninguna parte del código. Si en el futuro se
  // reintroduce un sistema de error counting, agregarlo aquí.
}

// --- Deduplicación de requests (evita clicks rápidos duplicados) ---
const pendingRequests = new Map();

function getRequestKey(config) {
  // Solo deduplicar GETs (las mutaciones POST/PUT/DELETE se permiten)
  if (config.method !== "get") return null;
  return `${config.method}:${config.baseURL || ""}${
    config.url
  }:${JSON.stringify(config.params || {})}`;
}

// --- Axios setup ---
const HttpClient = axios.create({
  baseURL: VUE_APP_API_URL,
  withCredentials: false,
  headers: {
    Accept: "application/json",
    Authorization: `Bearer ${user.token}`
  }
});

// Dynamic token + inactivity check + dedup on every request
HttpClient.interceptors.request.use(config => {
  // Check inactivity — nunca bloquear el endpoint de login
  const isLoginRequest = config.url && config.url.includes("/login");
  if (!isLoginRequest && checkInactivity()) {
    return Promise.reject(new axios.Cancel("Session expired"));
  }

  const currentUser = JSON.parse(localStorage.getItem("user")) || {};
  if (currentUser.token) {
    // Si el token no tiene formato JWT válido → logout inmediato
    if (currentUser.token.split(".").length !== 3) {
      forceLogout("Tu sesión ha expirado. Inicia sesión nuevamente.");
      return Promise.reject(new axios.Cancel("Invalid token"));
    }
    config.headers.Authorization = `Bearer ${currentUser.token}`;
  }

  // Update activity timestamp on every API call
  updateActivity();

  // Cancelar request GET duplicado en vuelo
  const key = getRequestKey(config);
  if (key) {
    if (pendingRequests.has(key)) {
      pendingRequests.get(key).cancel("Duplicate request cancelled");
    }
    const source = axios.CancelToken.source();
    config.cancelToken = config.cancelToken || source.token;
    pendingRequests.set(key, source);
  }

  return config;
});

HttpClient.interceptors.response.use(
  response => {
    // Limpiar de pending requests
    const key = getRequestKey(response.config);
    if (key) pendingRequests.delete(key);
    // Successful response: clear error counter
    clearErrors();
    return response;
  },
  error => {
    // Limpiar de pending requests
    if (error.config) {
      const key = getRequestKey(error.config);
      if (key) pendingRequests.delete(key);
    }

    if (axios.isCancel(error)) {
      return Promise.resolve({ data: {} }); // Silenciar requests cancelados
    }

    // Permitir que el componente maneje el error sin notificación del interceptor
    // El componente (ej: Copilot) se encarga de mostrar mensajes y manejar 401 internamente
    if (error.config?.skipErrorNotification) {
      return Promise.reject(error);
    }

    if (!error.response) {
      // La firma SRI recupera su resultado por su cuenta (ver signInvoiceSRI)
      if (error.config?.skipNetworkErrorNotification) {
        return Promise.reject(error);
      }
      // Errores de red NO son problemas de autenticación.
      // No forzamos logout — solo notificamos y dejamos que el usuario reintente.
      notifications.error({
        message: "Error de conexión. Verifica tu internet."
      });
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    const message = data?.message || "Ha ocurrido un error inesperado";

    switch (status) {
      case 401:
        forceLogout();
        break;
      case 403:
        if (data?.error === "plan_limit_reached") {
          require("@/store").default.dispatch("triggerLimitModal", {
            feature: data.feature,
            usage: data.usage
          });
          break;
        }
        // Multi-usuario: el backend explica qué falta (rol o punto de emisión).
        notifications.error({
          message:
            data?.message || "No tienes permiso para realizar esta acción"
        });
        break;
      case 422:
        if (data?.errors) {
          const firstError = Object.values(data.errors)[0];
          notifications.error({
            message: Array.isArray(firstError) ? firstError[0] : firstError
          });
        } else {
          notifications.error({ message });
        }
        break;
      case 429:
        if (data?.error !== "limit_exceeded") {
          notifications.error({
            message:
              "Demasiadas solicitudes. Espera unos segundos e intenta de nuevo."
          });
        }
        break;
      case 500:
      case 502:
      case 503: {
        // Errores del servidor NO son problemas de autenticación.
        // Solo notificamos. El logout por 500s se hizo gatillador falso.
        notifications.error({
          message: "Error del servidor. Intenta nuevamente."
        });
        break;
      }
      default:
        notifications.error({ message });
        break;
    }

    return Promise.reject(error);
  }
);

export default () => {
  return HttpClient;
};

export const configFormData = {
  header: {
    "Content-Type": "multipart/form-data"
  }
};
